import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";

import ScrollToTop from "./ScrollToTop";
import ThemeToggleButton from "./ThemeToggleButton";
import "../styles/MainIssueList.css";
import {
  loadFromCache,
  saveToCache,
  getTimestamp,
  setTimestamp,
  isFresh,
} from "../utils/cache";
// import GoogleAd from "./GoogleAd";

const ISSUE_REFRESH_SCHEDULE = [
  { hour: 10, minute: 0 },
  { hour: 18, minute: 0 },
];

const DAY_IN_MS = 24 * 60 * 60 * 1000;

function buildScheduledDate(baseDate, { hour, minute }) {
  return new Date(
    baseDate.getFullYear(),
    baseDate.getMonth(),
    baseDate.getDate(),
    hour,
    minute,
    0,
    0
  );
}

function getExpectedDailyIssueCutoff(now = new Date()) {
  const morningCutoff = buildScheduledDate(now, ISSUE_REFRESH_SCHEDULE[0]);
  const eveningCutoff = buildScheduledDate(now, ISSUE_REFRESH_SCHEDULE[1]);

  if (now.getTime() >= eveningCutoff.getTime()) {
    return eveningCutoff;
  }

  if (now.getTime() >= morningCutoff.getTime()) {
    return morningCutoff;
  }

  const yesterday = new Date(now.getTime() - DAY_IN_MS);
  return buildScheduledDate(yesterday, ISSUE_REFRESH_SCHEDULE[1]);
}

export default function MainIssueList() {
  const SortIcon = () => (
    <svg
      aria-hidden="true"
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="currentColor"
    >
      <path d="M7 4h2v12h3l-4 4-4-4h3V4zm6 16h2V8h3l-4-4-4 4h3v12z" />
    </svg>
  );

  const normalizeCount = (value) => {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string") {
      const numeric = parseInt(value.replace(/[^0-9]/g, ""), 10);
      return Number.isNaN(numeric) ? 0 : numeric;
    }
    return 0;
  };

  const [issues, setIssues] = useState(() => {
    const cachedIssues = loadFromCache("cachedIssueSummaries");
    if (cachedIssues && isFresh(getTimestamp("issueSummariesTimestamp"))) {
      return Array.isArray(cachedIssues) ? cachedIssues : [];
    }
    return [];
  });
  const [isLoading, setIsLoading] = useState(false);

  const [dailyIssuesByCategory, setDailyIssuesByCategory] = useState(() => {
    const cachedIssues = loadFromCache("cachedDailyIssuesByCategory");
    if (
      cachedIssues &&
      isFresh(getTimestamp("dailyIssuesByCategoryTimestamp"))
    ) {
      return cachedIssues;
    }
    return null;
  });

  const latestIssueSavedAt = useMemo(() => {
    let latestMs = 0;

    const categories = dailyIssuesByCategory?.byCategory;
    if (categories) {
      Object.values(categories).forEach((issueList) => {
        (issueList || []).forEach((item) => {
          if (!item?.savedTime) return;
          const parsed = Date.parse(item.savedTime);
          if (Number.isNaN(parsed)) return;
          if (parsed > latestMs) {
            latestMs = parsed;
          }
        });
      });
    }

    if (latestMs) {
      return new Date(latestMs);
    }

    const rawDate = dailyIssuesByCategory?.date;
    if (typeof rawDate === "string" && rawDate.length === 8) {
      const year = Number.parseInt(rawDate.slice(0, 4), 10);
      const month = Number.parseInt(rawDate.slice(4, 6), 10) - 1;
      const day = Number.parseInt(rawDate.slice(6, 8), 10);
      if (
        Number.isFinite(year) &&
        Number.isFinite(month) &&
        Number.isFinite(day)
      ) {
        return new Date(year, month, day);
      }
    }

    return null;
  }, [dailyIssuesByCategory]);

  const latestIssueTimestampLabel = useMemo(() => {
    if (!latestIssueSavedAt) return null;

    const formatter = new Intl.DateTimeFormat("ko-KR", {
      timeZone: "Asia/Seoul",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    const parts = formatter.formatToParts(latestIssueSavedAt);
    const getVal = (type) =>
      parts.find((part) => part.type === type)?.value || "";

    return `${getVal("year")}.${getVal("month")}.${getVal("day")} ${getVal(
      "hour"
    )}:${getVal("minute")}`;
  }, [latestIssueSavedAt]);

  const isDailyIssuesDataFresh = () => {
    if (!dailyIssuesByCategory || !latestIssueSavedAt) return false;
    const expectedCutoff = getExpectedDailyIssueCutoff();
    return latestIssueSavedAt.getTime() >= expectedCutoff.getTime();
  };

  const [selectedCategory, setSelectedCategory] = useState("all");
  const [sortMode, setSortMode] = useState("rank"); // "count" | "rank"

  const CATEGORY_LABELS = {
    politics: "정치",
    // economy: '경제',
    // society: '사회',
    // culture: '문화',
    global: "국제",
    // region: '지역',
    // sports: '스포츠',
    // it: 'IT과학',
  };

  const [outlets, setOutlets] = useState(() => {
    const cachedOutlets = loadFromCache("cachedOutlets");
    if (cachedOutlets && isFresh(getTimestamp("outletsTimestamp"))) {
      return cachedOutlets;
    }
    return {};
  });

  const bootstrappedRef = useRef(false);
  const refreshTimerRef = useRef(null);

  const scheduleNextRefresh = () => {
    const now = new Date();
    const candidates = [];

    ISSUE_REFRESH_SCHEDULE.forEach(({ hour, minute }) => {
      const candidate = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        hour,
        minute,
        0,
        0
      );
      if (candidate.getTime() > now.getTime()) {
        candidates.push(candidate);
      }
    });

    if (candidates.length === 0) {
      const { hour, minute } = ISSUE_REFRESH_SCHEDULE[0];
      const tomorrow = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() + 1,
        hour,
        minute,
        0,
        0
      );
      candidates.push(tomorrow);
    }

    const next = candidates.sort((a, b) => a.getTime() - b.getTime())[0];
    const delay = next.getTime() - now.getTime();

    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }

    refreshTimerRef.current = setTimeout(() => {
      bootstrappedRef.current = false;
      localStorage.removeItem("cachedIssueSummaries");
      localStorage.removeItem("issueSummariesTimestamp");
      localStorage.removeItem("cachedDailyIssuesByCategory");
      localStorage.removeItem("dailyIssuesByCategoryTimestamp");
      localStorage.removeItem("cachedOutlets");
      localStorage.removeItem("outletsTimestamp");
      setIssues([]);
      setDailyIssuesByCategory(null);
      setOutlets({});
      scheduleNextRefresh();
    }, delay);
  };

  useEffect(() => {
    scheduleNextRefresh();
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const hasFreshIssues =
      Array.isArray(issues) &&
      issues.length > 0 &&
      isFresh(getTimestamp("issueSummariesTimestamp"));
    const hasFreshOutlets =
      Object.keys(outlets).length > 0 &&
      isFresh(getTimestamp("outletsTimestamp"));
    const hasFreshCategoryIssues = isDailyIssuesDataFresh();

    if (hasFreshIssues && hasFreshOutlets && hasFreshCategoryIssues) {
      return;
    }

    if (bootstrappedRef.current) return;
    bootstrappedRef.current = true;

    (async () => {
      setIsLoading(true);
      try {
        const [issuesRes, dailyRes, outletsRes] = await Promise.all([
          fetch("/api/issues/summary"),
          fetch("/api/daily-issues/by-category"),
          fetch("/api/outlets/compact"),
        ]);

        if (!issuesRes.ok) {
          throw new Error(`issues summary error: ${issuesRes.status}`);
        }
        if (!dailyRes.ok) {
          throw new Error(`daily issues error: ${dailyRes.status}`);
        }
        if (!outletsRes.ok) {
          throw new Error(`outlets error: ${outletsRes.status}`);
        }

        const issuesData = await issuesRes.json();
        const issuesList = Array.isArray(issuesData?.issues)
          ? issuesData.issues
          : [];
        setIssues(issuesList);
        saveToCache("cachedIssueSummaries", issuesList);
        setTimestamp("issueSummariesTimestamp");

        const categoryData = await dailyRes.json();
        setDailyIssuesByCategory(categoryData);
        saveToCache("cachedDailyIssuesByCategory", categoryData);
        setTimestamp("dailyIssuesByCategoryTimestamp");

        const outletsArray = await outletsRes.json();
        const outletsData = {};
        (Array.isArray(outletsArray) ? outletsArray : []).forEach((doc) => {
          if (doc?.id) {
            outletsData[doc.id] = doc;
          }
        });
        setOutlets(outletsData);
        saveToCache("cachedOutlets", outletsData);
        setTimestamp("outletsTimestamp");
      } catch (e) {
        console.error("Initial data fetch failed:", e);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [issues, outlets, dailyIssuesByCategory, latestIssueSavedAt]);


  const issueRankIndex = useMemo(() => {
    const map = new Map();
    const byCategory = dailyIssuesByCategory?.byCategory || {};
    Object.entries(byCategory).forEach(([category, items]) => {
      (items || []).forEach((item, idx) => {
        if (!item?.issue) return;
        const normalized = normalizeCount(item?.count);
        const existing = map.get(item.issue) || [];
        existing.push({ category, rank: idx, count: normalized });
        map.set(item.issue, existing);
      });
    });
    return map;
  }, [dailyIssuesByCategory]);

  const getBestRank = (keyword, category) => {
    const entries = issueRankIndex.get(keyword) || [];
    if (category && category !== "all") {
      const scoped = entries.filter((entry) => entry.category === category);
      if (scoped.length === 0) return null;
      return scoped.reduce(
        (best, entry) =>
          best === null || entry.rank < best.rank ? entry : best,
        null
      );
    }
    if (entries.length === 0) return null;
    return entries.reduce(
      (best, entry) => (best === null || entry.rank < best.rank ? entry : best),
      null
    );
  };

  const getIssueTotalCount = (issueDoc) => {
    const counts = issueDoc?.articleCounts || {};
    return Object.values(counts).reduce(
      (acc, value) => acc + normalizeCount(value),
      0
    );
  };

  const sortedIssues = useMemo(() => {
    const baseIssues =
      selectedCategory === "all"
        ? issues
        : issues.filter((doc) => doc.category === selectedCategory);

    const withIndex = baseIssues.map((issue, idx) => ({ issue, idx }));

    const compareByCount = (a, b) => {
      const countDiff =
        getIssueTotalCount(b.issue) - getIssueTotalCount(a.issue);
      if (countDiff !== 0) return countDiff;
      const dateDiff = (b.issue.keyDate || "").localeCompare(
        a.issue.keyDate || ""
      );
      if (dateDiff !== 0) return dateDiff;
      return a.idx - b.idx;
    };

    const compareByRank = (a, b, categoryForRank) => {
      const rankA = getBestRank(a.issue.keyword, categoryForRank);
      const rankB = getBestRank(b.issue.keyword, categoryForRank);
      const rankValA = rankA ? rankA.rank : Number.POSITIVE_INFINITY;
      const rankValB = rankB ? rankB.rank : Number.POSITIVE_INFINITY;

      if (rankValA !== rankValB) return rankValA - rankValB;

      const rankCountA = rankA ? rankA.count : 0;
      const rankCountB = rankB ? rankB.count : 0;
      if (rankCountA !== rankCountB) return rankCountB - rankCountA;

      const countDiff =
        getIssueTotalCount(b.issue) - getIssueTotalCount(a.issue);
      if (countDiff !== 0) return countDiff;

      const dateDiff = (b.issue.keyDate || "").localeCompare(
        a.issue.keyDate || ""
      );
      if (dateDiff !== 0) return dateDiff;
      return a.idx - b.idx;
    };

    if (sortMode !== "rank") {
      return withIndex.sort(compareByCount).map(({ issue }) => issue);
    }

    // rank 모드: 날짜별(최신순)로, 카테고리 순서대로 10개씩 쌓으며 최대 5일치
    const dateOrder = Array.from(
      new Set(
        baseIssues
          .map((doc) => doc.keyDate)
          .filter(Boolean)
          .sort((a, b) => (b || "").localeCompare(a || ""))
      )
    ).slice(0, 5);

    const categoryOrder =
      selectedCategory === "all" ? ["politics", "global"] : [selectedCategory];

    const result = [];

    dateOrder.forEach((date) => {
      categoryOrder.forEach((category) => {
        const perDateCategory = withIndex
          .filter(
            ({ issue }) =>
              issue.keyDate === date && issue.category === category
          )
          .sort((a, b) => compareByRank(a, b, category))
          .slice(0, 10)
          .map(({ issue }) => issue);
        result.push(...perDateCategory);
      });
    });

    return result;
  }, [issues, selectedCategory, sortMode, issueRankIndex]);

  const navigate = useNavigate();

  const HERO_COUNT = 1;
  const GRID_BATCH_SIZE = 6;
  const INITIAL_VISIBLE = HERO_COUNT + GRID_BATCH_SIZE;

  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);

  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE);
  }, [selectedCategory, sortMode]);

  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + GRID_BATCH_SIZE);
  };

  const visibleIssueKeywords = sortedIssues.map((doc) => doc.keyword);
  const currentIssues = visibleIssueKeywords.slice(0, visibleCount);
  const isLoadingIssues = isLoading && currentIssues.length === 0;

  const getIssueData = (keyword) =>
    issues.find((doc) => doc.keyword === keyword) || null;

  const defaultRepresentatives = {
    progressive: null,
    moderate: null,
    conservative: null,
    unknown: null,
  };

  const defaultCounts = {
    progressive: 0,
    moderate: 0,
    conservative: 0,
    unknown: 0,
  };

  const getRepresentativeArticles = (keyword) => {
    const issueData = getIssueData(keyword);
    if (!issueData) return defaultRepresentatives;
    return {
      ...defaultRepresentatives,
      ...(issueData.representatives || {}),
    };
  };

  const getArticleCounts = (keyword) => {
    const issueData = getIssueData(keyword);
    if (!issueData) return defaultCounts;
    return {
      ...defaultCounts,
      ...(issueData.articleCounts || {}),
    };
  };

  const getCoverImage = (representatives) => {
    const order = ["progressive", "moderate", "conservative", "unknown"];
    for (const key of order) {
      const rep = representatives[key];
      if (rep?.imageUrl) {
        return {
          image: rep.imageUrl,
          source: rep.outletName || null,
        };
      }
    }
    return { image: null, source: null };
  };

  const handleIssueClick = (issue) => {
    const matched = getIssueData(issue);
    navigate(`/issue/${encodeURI(issue)}`, {
      state: {
        summary: matched?.summary || null,
        articleCounts: matched?.articleCounts || defaultCounts,
        representatives: matched?.representatives || defaultRepresentatives,
      },
    });
  };

  const handleKeywordClick = (keyword) => {
    const matchingDoc = issues.find(
      (doc) => typeof doc.keyword === "string" && doc.keyword === keyword.issue
    );
    handleIssueClick(matchingDoc?.keyword || keyword.issue);
  };

  return (
    <>
      <div className="main-issues-layout">
        <div className="news-portal">
          <div className="controls-bar">
            <div className="category-selector">
              <button
                onClick={() => setSelectedCategory("all")}
                className={`category-button ${
                  selectedCategory === "all" ? "active" : ""
                }`}
              >
                종합
              </button>
              {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setSelectedCategory(key)}
                  className={`category-button ${
                    selectedCategory === key ? "active" : ""
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="sort-selector">
              <button
                className="sort-trigger"
                aria-label="정렬 변경"
                onClick={() =>
                  setSortMode((prev) => (prev === "count" ? "rank" : "count"))
                }
              >
                <span className="sort-icon">
                  <SortIcon />
                </span>
                {sortMode === "count" ? "기사 수 순" : "이슈 순위 순"}
              </button>
            </div>
          </div>

          {/* {latestIssueTimestampLabel && (
            <div className="data-freshness">
              <span className="data-freshness__label">최근 수집 시각</span>
              <span className="data-freshness__value">
                {latestIssueTimestampLabel} 
              </span>
            </div>
          )} */}

          <div className="daily-keywords">
            <span className="keyword-label">오늘의 주요 이슈 :</span>
            <div className="keyword-list">
              {(() => {
                if (!dailyIssuesByCategory?.byCategory)
                  return <span>주요 이슈를 불러오는 중...</span>;

                const keywords =
                  selectedCategory === "all"
                    ? ["politics", "global"]
                        .flatMap(
                          (category) =>
                            dailyIssuesByCategory.byCategory[category] || []
                        )
                        .map((item) => ({
                          ...item,
                          count: normalizeCount(item?.count),
                        }))
                        .sort((a, b) => b.count - a.count)
                        .slice(0, 20)
                    : dailyIssuesByCategory.byCategory[selectedCategory] || [];

                if (keywords.length === 0) {
                  return (
                    <span className="no-keywords">
                      선택된 분야의 주요 이슈가 없습니다.
                    </span>
                  );
                }

                return keywords.map((item, idx) => (
                  <span
                    key={item.issue ?? idx}
                    className="keyword"
                    onClick={() => handleKeywordClick({ issue: item.issue })}
                  >
                    {item.issue}
                  </span>
                ));
              })()}
            </div>
          </div>

          {currentIssues.length > 0 ? (
            <>
              <section className="hot-topics">
                {currentIssues.slice(0, 1).map((issue, index) => {
                  const representatives = getRepresentativeArticles(issue);
                  const articleCounts = getArticleCounts(issue);
                  const { image, source } = getCoverImage(representatives);
                  const backgroundImage = image
                    ? `url(${image})`
                    : "url(https://via.placeholder.com/1200x600?text=No+Image)";

                  return (
                    <div
                      key={index}
                      className="featured-article"
                      onClick={() => handleIssueClick(issue)}
                    >
                      <div
                        className="featured-image"
                        style={{ backgroundImage }}
                      >
                        {source && (
                          <div className="image-source">사진 출처: {source}</div>
                        )}
                        <div className="featured-content">
                          <h3>{issue}</h3>
                          <div className="featured-articles">
                            {["progressive", "moderate", "conservative"].map(
                              (biasKey) => {
                                const rep = representatives[biasKey];
                                const biasLabel =
                                  biasKey === "progressive"
                                    ? "진보"
                                    : biasKey === "moderate"
                                    ? "중도"
                                    : "보수";
                                return (
                                  <div
                                    key={biasKey}
                                    className="featured-article-item"
                                  >
                                    <span
                                      className={`bias-tag ${biasKey}`}
                                    >
                                      {biasLabel} | {articleCounts[biasKey] || 0}
                                    </span>
                                    {rep ? (
                                      <p className="article-title">{rep.title}</p>
                                    ) : (
                                      <p className="article-title">관련 기사 없음</p>
                                    )}
                                    <span className="article-outlet">
                                      {rep?.outletName || null}
                                    </span>
                                  </div>
                                );
                              }
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </section>

              <section className="latest-news">
                <div className="news-grid">
                  {currentIssues.slice(1).map((issue, index) => {
                    const representatives = getRepresentativeArticles(issue);
                    const articleCounts = getArticleCounts(issue);
                    const { image, source } = getCoverImage(representatives);
                    const backgroundImage = image
                      ? `url(${image})`
                      : "url(https://via.placeholder.com/600x400?text=No+Image)";

                    return (
                      <div
                        key={index}
                        className="news-card"
                        onClick={() => handleIssueClick(issue)}
                      >
                        <div
                          className="news-image"
                          style={{ backgroundImage }}
                        >
                          {source && (
                            <div className="image-source">사진 출처: {source}</div>
                          )}
                        </div>
                        <div className="news-content">
                          <h3>{issue}</h3>
                          <div className="article-previews">
                            {["progressive", "moderate", "conservative"].map(
                              (biasKey) => {
                                const rep = representatives[biasKey];
                                const biasLabel =
                                  biasKey === "progressive"
                                    ? "진보"
                                    : biasKey === "moderate"
                                    ? "중도"
                                    : "보수";
                                return (
                                  <div
                                    key={biasKey}
                                    className="article-preview-item"
                                  >
                                    <span
                                      className={`bias-tag ${biasKey}`}
                                    >
                                      {biasLabel} | {articleCounts[biasKey] || 0}
                                    </span>
                                    {rep ? (
                                      <p className="preview-title">{rep.title}</p>
                                    ) : (
                                      <p className="preview-title">관련 기사 없음</p>
                                    )}
                                    <span className="preview-outlet">
                                      {rep?.outletName || null}
                                    </span>
                                  </div>
                                );
                              }
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>

              {visibleCount < sortedIssues.length && (
                <div style={{ textAlign: "center", margin: "40px 0" }}>
                  <button className="page-button" onClick={handleLoadMore}>
                    더 보기
                  </button>
                </div>
              )}
            </>
          ) : isLoadingIssues ? (
            <div className="main-loading" role="status" aria-live="polite">
              <div className="main-loading__spinner" aria-hidden="true" />
              <div className="main-loading__text">
                <h2>메인 기사 카드 불러오는 중...</h2>
                <p>잠시만 기다려 주세요.</p>
              </div>
            </div>
          ) : (
            <div className="no-articles">
              <p>선택하신 카테고리에 해당하는 기사가 없습니다.</p>
            </div>
          )}
        </div>
        {/* <aside className="ad-column ad-left" aria-label="좌측 광고">
          <GoogleAd
            adClient="ca-pub-1885844100156878"
            adSlot="9652467301"
            adFormat="auto"
            fullWidthResponsive
            // style={{ width: "100%", maxWidth: "150px" }}
            placeholder="개발 환경에서는 광고 대신 이 플레이스홀더가 표시됩니다."
          />
        </aside>
        <aside className="ad-column ad-right" aria-label="우측 광고">
          <GoogleAd
            adClient="ca-pub-1885844100156878"
            adSlot="2228261098"
            adFormat="auto"
            fullWidthResponsive
            // style={{ width: "100%", maxWidth: "150px" }}
            placeholder="개발 환경에서는 광고 대신 이 플레이스홀더가 표시됩니다."
          />
        </aside> */}
      </div>
      {/* <div className="ad-bottom">
        <GoogleAd
          adClient="ca-pub-1885844100156878"
          adSlot="5358639705"
          adFormat="auto"
          fullWidthResponsive
          placeholder="개발 환경에서는 광고 대신 이 플레이스홀더가 표시됩니다."
        />
      </div> */}
      <ThemeToggleButton />
      <ScrollToTop />
    </>
  );
}
